import { Injectable } from '@angular/core';
import { Observable, Subject, Subscription } from 'rxjs';
import { first } from 'rxjs/operators';
import { Dictionary } from '../../models/dictionary.model';
import { ActionTopic } from './action-topic.enum';
import { IDispatcherAction } from './idispatcher-action.interface';
import { MessageModel } from './message.model';

@Injectable()
export class DispatcherService {
    private actionCollection: Dictionary;
    private listeners: Dictionary;
    private subscriptions: Dictionary;
    private nextId: number;
    public publishedMessages: MessageModel[] = [];

    /**
     * Initializes a new instance of the DispatcherService.
     */
    constructor() {
        this.actionCollection = new Dictionary([]);
        this.listeners = new Dictionary([]);
        this.subscriptions = new Dictionary([]);
        this.nextId = 2;
    }

    /**
     * Adds a listener for the topic.  Any message that is sent with the topic will be given to the listener function provided.
     * @param component: The component that is currently using the Dispatcher.
     * @param listener: Event handler for the message.  This function will be called for any message that matches the topic specified.
     * @param topic: The topic to listen for.
     */
    subscribe(component: any, listener: ((message: MessageModel) => void), topic: ActionTopic,
        replayLastMessageKeys: string[] = undefined): void {

        const self = this;

        if (!this.listeners.containsKey(topic.toString())) {
            const listenerSubject = new Subject<MessageModel>();
            this.listeners.add(topic.toString(), listenerSubject);
        }

        if (!component.__proto__.dispatcherId) {
            component.__proto__.dispatcherId = this.getNextId();
            const subscriptionArray: Subscription[] = new Array<Subscription>();
            this.subscriptions.add(component.__proto__.dispatcherId.toString(), subscriptionArray);
        } else if (!this.subscriptions[component.__proto__.dispatcherId.toString()]) {
            const subscriptionArray: Subscription[] = new Array<Subscription>();
            this.subscriptions.add(component.__proto__.dispatcherId.toString(), subscriptionArray);
        }

        const newSubscription: Subscription = this.listeners[topic.toString()].subscribe(listener);
        this.subscriptions[component.__proto__.dispatcherId.toString()].push(newSubscription);

        if (replayLastMessageKeys != undefined) {
            replayLastMessageKeys.forEach(function(key) {
                // find last message of this topic and emit it, if it exists
                if (self.publishedMessages != undefined) {
                    const filteredPublishedMessages = self.publishedMessages.filter(m => m.topic == topic && m.values != undefined && m.values.containsKey(key));
                    if (filteredPublishedMessages != undefined && filteredPublishedMessages.length > 0) {
                        const lastMessage = filteredPublishedMessages[filteredPublishedMessages.length - 1];
                        if (lastMessage != undefined) {
                            self.listeners[lastMessage.topic.toString()].next(lastMessage);
                        }
                    }
                }
            });
        }
    }

    /**
     * Removes all listeners for all topic on the component.
     * @param component: The component instance that will have all listeners removed.
     */
    unsubscribe(component: any): void {
        if (component && component.__proto__ && component.__proto__.dispatcherId) {
            const dispatcherId: string = component.__proto__.dispatcherId.toString();
            if (this.subscriptions.containsKey(dispatcherId)) {
                const subscriptionArray: Subscription[] = this.subscriptions[dispatcherId];
                for (let i = 0; i < subscriptionArray.length; i++) {
                    subscriptionArray[i].unsubscribe();
                }

                this.subscriptions.remove(dispatcherId);
            }
        }
    }

    /**
     * Publishes the message out to any subscribers of the message topic.
     * @param message: Message to publish
     */
    publish(message: MessageModel): void {
        this.publishedMessages.push(message);
        if (this.listeners[message.topic.toString()]) {
            this.listeners[message.topic.toString()].next(message);
        }
    }

    /**
     * Creates a dispatcher action that can be used in an action collection.
     * @param topic: Topic of the message sent with the action.
     * @param action: Delegate function to run when the action is performed.  May be null.
     * @returns: Created dispatcher action.
     */
    createDispatcherAction(topic: ActionTopic, action: ((...args: any[]) => Observable<any>), actionArgs?: Dictionary, parameterMap?: (results: any[], args: Dictionary) => void): IDispatcherAction {
        const message: MessageModel = new MessageModel();
        message.topic = topic;
        const newAction: IDispatcherAction = { message: message, action: action, actionParameters: actionArgs, parameterMap: parameterMap };
        return newAction;
    }

    /**
     * Adds an action set to be run and issue a unique identifier that can be used to invoke the action set.
     * @returns: Unique identifier for the action set.
     */
    addActionSet(actions: IDispatcherAction[]): number {
        const newId: number = this.getNextId();
        this.actionCollection.add(newId.toString(), actions);
        return newId;
    }

    /**
     * Invokes the action set
     * @param actionSetId: Identifier of the action set to run.
     * @returns: Array of results.  Each action will have an entry in the array, corresponding to the position
     * the action was run in (ie, first action performed will have an entry in position 0, etc).
     */
    async invokeActionSet(actionSetId: number, failureCallback?: (functionName: string, error: any) => void): Promise<any[]> {
        const resultSet: any[] = new Array<any>();

        if (this.actionCollection.containsKey(actionSetId.toString())) {
            const actions: IDispatcherAction[] = this.actionCollection[actionSetId.toString()];
            await this.performActions(actions, resultSet, failureCallback);
        }

        return resultSet;
    }

    /**
     * Invokes the action set with the details included in every message emitted.
     * @param actionSetId: Identifier of the action set to run.
     * @param messageDetails: Details that will be attached to each message emitted
     * @returns: Array of results.  Each action will have an entry in the array, corresponding to the position
     * the action was run in (ie, first action performed will have an entry in position 0, etc).
     */
    async invokeActionSetWithDetails(actionSetId: number, messageDetails: Dictionary, failureCallback?: (functionName: string, error: any) => void): Promise<any[]> {
        const resultSet: any[] = new Array<any>();

        if (this.actionCollection.containsKey(actionSetId.toString())) {
            const actions: IDispatcherAction[] = this.actionCollection[actionSetId.toString()];

            for (var i = 0; i < actions.length; i++) {
                for (var j = 0; j < messageDetails.getKeys().length; j++) {
                    const key: string = messageDetails.getKeys()[j];
                    actions[i].message.values.add(key, messageDetails[key]);
                }
            }

            await this.performActions(actions, resultSet, failureCallback);
        }

        return resultSet;
    }

    /**
     * Performs the actions in the array in the order they appear in the array.  Will wait for completion of one action
     * before starting the next.  If one action fails, execution stops.
     * @param actionArray: Array of actions to execute.
     * @param resultSet: Array that will have the results of each action execution pushed into it.
     */
    private async performActions(actionArray: IDispatcherAction[], resultSet: any[], failureCallback?: (functionName: string, error: any) => void) {
        for (var i = 0; i < actionArray.length; i++) {
            if (actionArray[i].action) {
                let result = true;
                try {
                    const arr: any[] = new Array<any>();

                    if (actionArray[i].parameterMap) {
                        actionArray[i].parameterMap(resultSet, actionArray[i].actionParameters);
                    }

                    if (actionArray[i].actionParameters) {
                        for (var j = 0; j < actionArray[i].actionParameters.getKeys().length; j++) {
                            const key = actionArray[i].actionParameters.getKeys()[j];
                            arr.push(actionArray[i].actionParameters[key]);
                        }
                    }

                    const obs = actionArray[i].action(...arr);
                    result = await obs.pipe(first()).toPromise();
                } catch (e) {
                    console.error(e);

                    if (failureCallback) {
                        failureCallback(actionArray[i].action.name, e);
                    }

                    throw e;
                }

                if (!result) {
                    resultSet.push(null);
                    return;
                }

                resultSet.push(result);
            }

            this.publish(actionArray[i].message);
        }
    }

    /**
     * Gets the next available identifier.
     * @returns: The next available identifier.
     */
    private getNextId(): number {
        return this.nextId++;
    }
}
